import { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import dayjs, { type Dayjs } from "dayjs";
import {
  selectBizMobileDashboardLoading,
  selectBizMobileOutputMetadata,
  selectBizMobileQueryOutput,
  selectBizMobileComparisonQueryOutput,
  selectBizMobileComparisonLoading,
} from "../../../../../../../features/bizDevelopmentDashboardMobileSlice";
import { useBizDevelopmentDashboardMobile } from "../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboardMobile";
import {
  buildColumns,
  exportToCSV,
  sortRows,
} from "../../../../helpers/bizDevelopmentDashboardHelpers";
import { spacing } from "../../../../../../../assets/styles/tokens";
import PerformanceDateFilter from "./PerformanceMobileContent/PerformanceDateFilter";
import PerformanceItem from "./PerformanceMobileContent/PerformanceItem";
import MobileFloatingActionButton from "../../../../../../../pages/FloatingItems/MobileFloatingActionButton";
import {
  type CommittedSelection,
  type CompareTimeframeValue,
  type PrimaryTimeframeValue,
  getCompareLabel,
  getPrimaryLabel,
} from "../../../../helpers/timeFrameCompareState";

const PRIMARY_VALUES: PrimaryTimeframeValue[] = [
  "yesterday",
  "two_days_ago",
  "three_days_ago",
  "mtd",
  "custom",
];
const COMPARE_VALUES: CompareTimeframeValue[] = [
  "yesterday",
  "two_days_ago",
  "three_days_ago",
];

const Performance = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const queryOutput = useSelector(selectBizMobileQueryOutput);
  const outputMetadata = useSelector(selectBizMobileOutputMetadata);
  const isLoading = useSelector(selectBizMobileDashboardLoading);
  const comparisonQueryOutput = useSelector(
    selectBizMobileComparisonQueryOutput,
  );
  const isComparisonLoading = useSelector(selectBizMobileComparisonLoading);

  const {
    fetchDashboardData,
    fetchComparisonData,
    clearComparisonData,
    getSiteFromWorkspace,
  } = useBizDevelopmentDashboardMobile();

  const buildCustomRange = (start: Dayjs | null, end: Dayjs | null) =>
    start && end
      ? { startDate: start.format("YYYY-MM-DD"), endDate: end.format("YYYY-MM-DD") }
      : undefined;

  const [committed, setCommitted] = useState<CommittedSelection>(() => {
    const primaryParam =
      searchParams.get("timeframePerformance") ?? "yesterday";
    const primary = (PRIMARY_VALUES as string[]).includes(primaryParam)
      ? (primaryParam as PrimaryTimeframeValue)
      : "yesterday";
    const customStartParam = searchParams.get("customStartPerformance");
    const customEndParam = searchParams.get("customEndPerformance");
    const compareParam = searchParams.get("additionalTimeframePerformance");
    const compare = (COMPARE_VALUES as string[]).includes(compareParam ?? "")
      ? (compareParam as CompareTimeframeValue)
      : null;
    return {
      primary,
      customStart:
        primary === "custom" && customStartParam
          ? dayjs(customStartParam)
          : null,
      customEnd:
        primary === "custom" && customEndParam ? dayjs(customEndParam) : null,
      compareEnabled: primary !== "mtd" && primary !== "custom" && !!compare,
      compare,
    };
  });

  const workspaceParam = searchParams.get("workspace");
  const prevWorkspaceRef = useRef<string | null>(null);

  // Re-fetch whenever workspace changes (covers initial mount + workspace switch)
  useEffect(() => {
    const sites = getSiteFromWorkspace();
    if (sites.length === 0) return;

    const isWorkspaceChange =
      prevWorkspaceRef.current !== null &&
      prevWorkspaceRef.current !== workspaceParam;
    prevWorkspaceRef.current = workspaceParam;

    if (isWorkspaceChange) {
      // Workspace switched — clear comparison state and re-fetch
      setCommitted((prev) => ({ ...prev, compareEnabled: false, compare: null }));
      clearComparisonData();
      setSearchParams((prev) => {
        prev.delete("additionalTimeframePerformance");
        return prev;
      });
    } else if (committed.compareEnabled && committed.compare) {
      // Initial mount — restore comparison if it was already in URL
      fetchComparisonData(committed.compare, sites);
    }

    fetchDashboardData(
      committed.primary,
      sites,
      buildCustomRange(committed.customStart, committed.customEnd),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceParam]);

  const sortKey =
    searchParams.get("sortKeyPerformance") ?? "brand_display_name";
  const sortDir = (searchParams.get("sortDirPerformance") ?? "asc") as
    | "asc"
    | "desc";

  const isMtd = committed.primary === "mtd";
  const isCustom = committed.primary === "custom";
  const isComparisonActive = committed.compareEnabled && !!committed.compare;

  const sortedRows = useMemo(
    () => sortRows(queryOutput, sortKey, sortDir),
    [queryOutput, sortKey, sortDir],
  );

  const handleApply = (next: CommittedSelection) => {
    const primaryChanged =
      next.primary !== committed.primary ||
      next.customStart?.valueOf() !== committed.customStart?.valueOf() ||
      next.customEnd?.valueOf() !== committed.customEnd?.valueOf();
    const compareChanged =
      next.compareEnabled !== committed.compareEnabled ||
      next.compare !== committed.compare;

    setCommitted(next);
    setSearchParams((prev) => {
      prev.set("timeframePerformance", next.primary);
      if (next.primary === "custom" && next.customStart && next.customEnd) {
        prev.set(
          "customStartPerformance",
          next.customStart.format("YYYY-MM-DD"),
        );
        prev.set("customEndPerformance", next.customEnd.format("YYYY-MM-DD"));
      } else {
        prev.delete("customStartPerformance");
        prev.delete("customEndPerformance");
      }
      if (next.compareEnabled && next.compare) {
        prev.set("additionalTimeframePerformance", next.compare);
      } else {
        prev.delete("additionalTimeframePerformance");
      }
      return prev;
    });

    const sites = getSiteFromWorkspace();
    if (primaryChanged) {
      fetchDashboardData(
        next.primary,
        sites,
        buildCustomRange(next.customStart, next.customEnd),
      );
    }

    if (next.compareEnabled && next.compare) {
      if (compareChanged) {
        fetchComparisonData(next.compare, sites);
      }
    } else {
      clearComparisonData();
    }
  };

  const handleRefresh = () => {
    const sites = getSiteFromWorkspace();
    fetchDashboardData(
      committed.primary,
      sites,
      buildCustomRange(committed.customStart, committed.customEnd),
    );
    if (committed.compareEnabled && committed.compare) {
      fetchComparisonData(committed.compare, sites);
    }
  };

  const handleDownload = () => {
    const columns = buildColumns(outputMetadata, isMtd);
    exportToCSV({
      columns,
      primaryRows: queryOutput,
      comparisonRows: isComparisonActive ? (comparisonQueryOutput ?? []) : [],
      primaryLabel: getPrimaryLabel(
        committed.primary,
        committed.customStart,
        committed.customEnd,
      ),
      comparisonLabel: isComparisonActive
        ? getCompareLabel(committed.compare)
        : "",
      isMtd,
      filename: `performance_${committed.primary}${isComparisonActive ? `_vs_${committed.compare}` : ""}`,
    });
  };

  const handleSortApply = (key: string, dir: "asc" | "desc") => {
    setSearchParams((prev) => {
      prev.set("sortKeyPerformance", key);
      prev.set("sortDirPerformance", dir);
      return prev;
    });
  };

  return (
    <Box sx={{ px: 2 }}>
      <PerformanceDateFilter
        committed={committed}
        onApply={handleApply}
        hasData={sortedRows.length > 0}
        isApiLoading={isLoading || isComparisonLoading}
        onRefresh={handleRefresh}
        onDownload={handleDownload}
        brandCount={sortedRows.length}
        isMtd={isMtd}
        isCustom={isCustom}
        isComparisonActive={isComparisonActive}
        sortKey={sortKey}
        sortDir={sortDir}
        onSortApply={handleSortApply}
        outputMetadata={outputMetadata ?? {}}
      />
      <Box sx={{ mt: "-" + spacing.Spacing.md.$value }}>
        <PerformanceItem
          queryOutput={sortedRows}
          comparisonQueryOutput={comparisonQueryOutput ?? []}
          isLoading={isLoading}
          isComparisonLoading={isComparisonLoading}
          isMtd={isMtd}
          isComparisonActive={isComparisonActive}
          outputMetadata={outputMetadata ?? {}}
        />
      </Box>
      <MobileFloatingActionButton trackerDataId="812" saveTrackerDataId="813" />
    </Box>
  );
};

export default Performance;
